import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Divider,
  Avatar,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  LinearProgress,
  CircularProgress,
  Stack,
  Tab,
  Tabs
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Event as EventIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Photo as PhotoIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  Place as PlaceIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  Storage as StorageIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Mail as MailIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Directions as DirectionsIcon,
  Info as InfoIcon,
  DateRange as DateRangeIcon
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { db } from '../../../firebase/config';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { getOrganization } from '../../../services/organizationService';
import { fetchOrganizationMetrics } from '../../../services/organizationService';

interface DashboardMetrics {
  totalEvents: number;
  upcomingEvents: number;
  totalMembers: number;
  totalTrips: number;
  upcomingTrips: number;
  totalPhotos: number;
  totalInvitations: number;
  acceptedInvitations: number;
  storageUsed: number;
  uniqueAttendees: number;
  totalShares: number;
  clickThroughRate: number;
}

interface Activity {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  action: string;
  entityType: string;
  entityId: string;
  entityName: string;
  timestamp: Date;
  meta?: any;
}

const OrganizationDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { activeOrganization, isPersonalAccount, switchToOrganization } = useOrganization();
  
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalEvents: 0,
    upcomingEvents: 0,
    totalMembers: 0,
    totalTrips: 0,
    upcomingTrips: 0,
    totalPhotos: 0,
    totalInvitations: 0,
    acceptedInvitations: 0,
    storageUsed: 0,
    uniqueAttendees: 0,
    totalShares: 0,
    clickThroughRate: 0
  });
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [upcomingTrips, setUpcomingTrips] = useState<any[]>([]);
  const [showTripSection, setShowTripSection] = useState<boolean>(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Switch to organization context if needed
        if (isPersonalAccount) {
          await switchToOrganization(id);
        }
        
        // Get organization data
        const orgData = await getOrganization(id);
        if (!orgData) {
          setError('Organization not found');
          setLoading(false);
          return;
        }
        
        // Fetch metrics specifically for this organization
        const metricsData = await fetchOrganizationMetrics(id);
        setMetrics(metricsData);
        
        // Fetch recent activities for this organization
        const activitiesRef = collection(db, 'organizationActivities');
        const activitiesQuery = query(
          activitiesRef,
          where('organizationId', '==', id),
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        
        const activitiesSnapshot = await getDocs(activitiesQuery);
        const activitiesData: Activity[] = [];
        
        activitiesSnapshot.forEach((doc) => {
          const data = doc.data();
          activitiesData.push({
            id: doc.id,
            userId: data.userId,
            userName: data.userName,
            userAvatar: data.userAvatar,
            action: data.action,
            entityType: data.entityType,
            entityId: data.entityId,
            entityName: data.entityName,
            timestamp: data.timestamp?.toDate() || new Date(),
            meta: data.meta
          });
        });
        
        setRecentActivities(activitiesData);
        
        // Fetch upcoming events specifically for this organization
        const eventsRef = collection(db, 'events');
        const eventsQuery = query(
          eventsRef,
          where('organizationId', '==', id),
          where('startDate', '>=', new Date()),
          orderBy('startDate', 'asc'),
          limit(3)
        );
        
        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData: any[] = [];
        
        eventsSnapshot.forEach((doc) => {
          const data = doc.data();
          eventsData.push({
            id: doc.id,
            title: data.title,
            startDate: data.startDate?.toDate() || new Date(),
            location: data.location,
            attendees: data.participants?.length || 0
          });
        });
        
        setUpcomingEvents(eventsData);
        
        // Fetch upcoming trips specifically for this organization
        const tripsRef = collection(db, 'trips');
        const tripsQuery = query(
          tripsRef,
          where('organizationId', '==', id),
          where('startDate', '>=', new Date()),
          orderBy('startDate', 'asc'),
          limit(3)
        );
        
        const tripsSnapshot = await getDocs(tripsQuery);
        const tripsData: any[] = [];
        
        tripsSnapshot.forEach((doc) => {
          const data = doc.data();
          tripsData.push({
            id: doc.id,
            title: data.title,
            destination: data.destination,
            startDate: data.startDate?.toDate() || new Date(),
            endDate: data.endDate?.toDate() || new Date(),
            coverPhotoUrl: data.coverPhotoUrl,
            locations: data.locations?.length || 0,
            accommodations: data.accommodations?.length || 0
          });
        });
        
        setUpcomingTrips(tripsData);
        
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [id, isPersonalAccount, switchToOrganization]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const formatStorageSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (entityType: string) => {
    switch (entityType) {
      case 'event':
        return <EventIcon color="primary" />;
      case 'trip':
        return <DirectionsIcon color="secondary" />;
      case 'member':
        return <GroupIcon color="success" />;
      case 'photo':
        return <PhotoIcon color="info" />;
      case 'invite':
        return <MailIcon color="warning" />;
      default:
        return <InfoIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Organization Header */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 2,
          backgroundImage: activeOrganization?.coverUrl 
            ? `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${activeOrganization.coverUrl})`
            : 'linear-gradient(120deg, #2196F3, #21CBF3)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar 
            src={activeOrganization?.logoUrl} 
            sx={{ width: 80, height: 80, mr: 3, border: '2px solid white' }}
          >
            {activeOrganization?.name?.charAt(0) || 'O'}
          </Avatar>
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              {activeOrganization?.name || 'Organization Dashboard'}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontSize: '1.1rem' }}>
              {activeOrganization?.description || 'Organization dashboard and analytics'}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Chip 
            label={`${metrics.totalMembers} Members`} 
            icon={<PeopleIcon />} 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', px: 1, py: 0.5, fontSize: '1rem' }} 
          />
          <Chip 
            label={`${metrics.totalEvents} Events`} 
            icon={<EventIcon />} 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', px: 1, py: 0.5, fontSize: '1rem' }} 
          />
          <Chip 
            label={`${metrics.upcomingEvents} Upcoming`} 
            icon={<TimelineIcon />} 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', px: 1, py: 0.5, fontSize: '1rem' }} 
          />
          <Chip 
            label={`${metrics.uniqueAttendees} Unique Attendees`} 
            icon={<GroupIcon />} 
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', px: 1, py: 0.5, fontSize: '1rem' }} 
          />
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Metrics Cards - First Row */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Overview</Typography>
            <Box>
              <Button 
                size="medium" 
                variant={showTripSection ? "contained" : "outlined"} 
                color="primary"
                onClick={() => setShowTripSection(true)}
                sx={{ mr: 1 }}
              >
                Trips
              </Button>
              <Button 
                size="medium" 
                variant={!showTripSection ? "contained" : "outlined"} 
                color="primary"
                onClick={() => setShowTripSection(false)}
              >
                Events
              </Button>
            </Box>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <Paper 
                sx={{ 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: '1px solid #e0e9fb'
                }}
              >
                <Typography variant="subtitle1" color="textSecondary" fontWeight="500">
                  Total Events
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <EventIcon sx={{ fontSize: 48, mr: 2, color: 'primary.main' }} />
                  <Typography variant="h3" component="div" fontWeight="bold">
                    {metrics.totalEvents}
                  </Typography>
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{ mt: 'auto' }}>
                  {metrics.upcomingEvents} upcoming
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} lg={3}>
              <Paper 
                sx={{ 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: '1px solid #fde6e0'
                }}
              >
                <Typography variant="subtitle1" color="textSecondary" fontWeight="500">
                  Members
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <PeopleIcon sx={{ fontSize: 48, mr: 2, color: 'secondary.main' }} />
                  <Typography variant="h3" component="div" fontWeight="bold">
                    {metrics.totalMembers}
                  </Typography>
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{ mt: 'auto' }}>
                  {metrics.totalInvitations - metrics.acceptedInvitations} pending invites
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Paper 
                sx={{ 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: '1px solid #e0f7fa'
                }}
              >
                <Typography variant="subtitle1" color="textSecondary" fontWeight="500">
                  Unique Attendees
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <GroupIcon sx={{ fontSize: 48, mr: 2, color: 'info.main' }} />
                  <Typography variant="h3" component="div" fontWeight="bold">
                    {metrics.uniqueAttendees}
                  </Typography>
                </Box>
                <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center' }}>
                  <ArrowUpwardIcon sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body1" color="success.main">
                    {Math.round((metrics.uniqueAttendees / (metrics.totalMembers || 1)) * 100)}% of members
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Paper 
                sx={{ 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: '1px solid #e8f5e9'
                }}
              >
                <Typography variant="subtitle1" color="textSecondary" fontWeight="500">
                  Engagement Rate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <TrendingUpIcon sx={{ fontSize: 48, mr: 2, color: 'success.main' }} />
                  <Typography variant="h3" component="div" fontWeight="bold">
                    {Math.round(metrics.clickThroughRate * 100)}%
                  </Typography>
                </Box>
                <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1" color="textSecondary">
                    {metrics.totalShares} total shares
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Second Metrics Row */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <Paper 
                sx={{ 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: '1px solid #ede7f6'
                }}
              >
                <Typography variant="subtitle1" color="textSecondary" fontWeight="500">
                  Total Trips
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <DirectionsIcon sx={{ fontSize: 48, mr: 2, color: 'secondary.dark' }} />
                  <Typography variant="h3" component="div" fontWeight="bold">
                    {metrics.totalTrips}
                  </Typography>
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{ mt: 'auto' }}>
                  {metrics.upcomingTrips} upcoming
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} lg={3}>
              <Paper 
                sx={{ 
                  p: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: 160,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  border: '1px solid #f3e5f5'
                }}
              >
                <Typography variant="subtitle1" color="textSecondary" fontWeight="500">
                  Photos
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <PhotoIcon sx={{ fontSize: 48, mr: 2, color: 'primary.light' }} />
                  <Typography variant="h3" component="div" fontWeight="bold">
                    {metrics.totalPhotos}
                  </Typography>
                </Box>
                <Typography variant="body1" color="textSecondary" sx={{ mt: 'auto' }}>
                  {formatStorageSize(metrics.storageUsed)} storage used
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Third row: Upcoming Trips/Events */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {showTripSection ? "Upcoming Trips" : "Upcoming Events"}
            </Typography>
            <Button 
              size="small" 
              variant="text" 
              href={showTripSection ? `/organizations/${id}/trips` : `/organizations/${id}/events`}
              endIcon={<ArrowUpwardIcon sx={{ transform: 'rotate(45deg)' }} />}
            >
              View all
            </Button>
          </Box>

          <Grid container spacing={2}>
            {showTripSection ? (
              upcomingTrips.length > 0 ? (
                upcomingTrips.map((trip) => (
                  <Grid item xs={12} md={4} key={trip.id}>
                    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      <CardMedia
                        component="img"
                        sx={{ height: 160, objectFit: 'cover' }}
                        image={trip.coverPhotoUrl || '/images/trip-placeholder.jpg'}
                        alt={trip.title}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography component="div" variant="h6" noWrap>
                          {trip.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PlaceIcon fontSize="small" color="primary" sx={{ mr: 0.5 }} />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {trip.destination}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DateRangeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                          <Typography variant="body2" color="text.secondary">
                            {new Date(trip.startDate).toLocaleDateString()} - {new Date(trip.endDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button size="small" href={`/organizations/${id}/trips/${trip.id}`}>
                          View Trip
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                    <Typography color="text.secondary" mb={2}>No upcoming trips</Typography>
                    <Button 
                      variant="outlined" 
                      startIcon={<AddIcon />}
                      href={`/organizations/${id}/trips/new`}
                    >
                      Plan a Trip
                    </Button>
                  </Paper>
                </Grid>
              )
            ) : (
              upcomingEvents.length > 0 ? (
                upcomingEvents.map((event) => (
                  <Grid item xs={12} md={4} key={event.id}>
                    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography component="div" variant="h6">
                          {event.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PlaceIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {event.location?.name || 'No location'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DateRangeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(event.startDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Chip 
                            size="small" 
                            icon={<PeopleIcon fontSize="small" />} 
                            label={`${event.attendees} ${event.attendees === 1 ? 'attendee' : 'attendees'}`} 
                          />
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button size="small" href={`/organizations/${id}/events/${event.id}`}>
                          View Event
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                    <Typography color="text.secondary" mb={2}>No upcoming events</Typography>
                    <Button 
                      variant="outlined" 
                      startIcon={<AddIcon />}
                      href={`/organizations/${id}/events/new`}
                    >
                      Create Event
                    </Button>
                  </Paper>
                </Grid>
              )
            )}
          </Grid>
        </Grid>

        {/* Fourth row: Recent Activities */}
        <Grid item xs={12}>
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Recent Activity
            </Typography>
          </Box>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', maxHeight: 400, overflow: 'auto' }}>
            {recentActivities.length > 0 ? (
              <List>
                {recentActivities.map((activity) => (
                  <ListItem key={activity.id} alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar src={activity.userAvatar}>
                        {activity.userName.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 500 }}>
                          {activity.action}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {activity.userName}
                          </Typography>
                          {` — ${activity.entityName}`}
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {formatDate(activity.timestamp)}
                          </Typography>
                        </>
                      }
                    />
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      {getActivityIcon(activity.entityType)}
                    </ListItemIcon>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No recent activity</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrganizationDashboard;